import { ExperienceMasterData } from "@/composables/useExperienceMasterDataApi";
import { useHierarchicalGroupsMasterdata } from "@/features/masterdata/composables";
import type { HierarchicalGroup } from "@/types/generated/ExperienceMasterDataApi";
import { toValue, type MaybeRefOrGetter } from "vue";

/**
 * Filters a premades list by an item-name OR group-name match.
 *
 * Group-name matching needs a `code -> HierarchicalGroup` map. By default it uses the global
 * hierarchical-groups masterdata store, which is right for callers that grouped by that same
 * store. Callers that group/label from a DIFFERENT map (e.g. the RAW add-premade modal, which
 * receives its groups as a prop) must pass that same map here via `hierarchicalGroups` so the
 * displayed groups stay searchable — otherwise a valid displayed group name would never match.
 */
export function usePremadesFiltering(
  premades: Ref<ExperienceMasterData | null | undefined>,
  query: Ref<string>,
  hierarchicalGroups?: MaybeRefOrGetter<Map<string, HierarchicalGroup>>
) {
  const { hierarchicalGroups: hierarchicalGroupsMasterData } = useHierarchicalGroupsMasterdata();

  const matchingItems = computed(() => {
    if (!premades.value) {
      return null;
    }

    if (query.value === "") {
      return premades.value;
    }

    const lowercaseQuery = query.value.toLocaleLowerCase();
    const groupsMap = hierarchicalGroups != null ? toValue(hierarchicalGroups) : hierarchicalGroupsMasterData.value;
    const hierarchicalGroupsList = Array.from(groupsMap.values());
    const codesOfMatchingHierarchicalGroups = hierarchicalGroupsList
      .filter((group) => group.name.toLocaleLowerCase().includes(lowercaseQuery))
      .map((group) => group.code);

    return premades.value.filter((item) => {
      const groupMatch =
        item.hierarchical_group_code !== undefined &&
        codesOfMatchingHierarchicalGroups.includes(item.hierarchical_group_code);
      const premadeNameMatch = item.name?.toLocaleLowerCase().includes(lowercaseQuery);

      return groupMatch || premadeNameMatch;
    });
  });

  const count = computed(() => matchingItems.value?.length ?? 0);

  return {
    matchingItems,
    count,
  };
}
